import type { Auftrag, AuftragsTyp } from '@awf/shared';

/**
 * Im Vertrag enthaltene Durchlaeufe je Kalendermonat, ueber alle Workflows und Personen
 * (Vorgabe 2026-10-16). Eine Anzeige, keine Sperre: Steigt der Bedarf, waechst das Kontingent
 * ohne Unterbrechung mit. Deshalb faerbt ein Ueberschreiten nichts rot.
 */
export const NUTZUNGSKONTINGENT = 200;

export const KONTINGENT_TEXT = `${NUTZUNGSKONTINGENT} Durchläufe pro Monat sind enthalten. Steigt der Bedarf, passt sich das Kontingent ohne Unterbrechung des Betriebs flexibel an.`;

/** Ein Durchlauf ist ein angelegter Auftrag; gezaehlt wird im Monat seines Anlegens. */
export interface Monatsnutzung {
  /** `JJJJ-MM` in Ortszeit, als Schluessel und zum Sortieren. */
  monat: string;
  durchlaeufe: number;
  jeTyp: Record<AuftragsTyp, number>;
  /** Verschiedene Personen, die in diesem Monat angelegt haben. */
  personen: number;
}

export interface NutzungsFilter {
  typ: AuftragsTyp | 'alle';
  /** uid der anlegenden Person, `null` fuer alle. */
  person: string | null;
}

/**
 * Berliner Zeit, fest, nicht die Uhr des Geraets (Codex-Gate PR #91): Der Monat entscheidet ueber
 * Vertragsnutzung gegen das Kontingent, und die darf nicht davon abhaengen, in welcher Zeitzone
 * der Browser steht. Wer am 1. um 00:30 in Berlin anlegt, zaehlt zum neuen Monat.
 */
const BERLINER_MONAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
});

export function monatVon(zeitpunkt: Date): string {
  const teile = BERLINER_MONAT.formatToParts(zeitpunkt);
  const teil = (art: string) => teile.find((t) => t.type === art)?.value ?? '';
  return `${teil('year')}-${teil('month')}`;
}

/** Reine Kalenderrechnung auf `JJJJ-MM`, ohne Zeitzone. */
export function vorMonat(monat: string, schritte: number): string {
  const [jahr, mm] = monat.split('-').map(Number) as [number, number];
  const index = jahr * 12 + (mm - 1) - schritte;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

// In UTC benannt, damit die Mitte des Monats nirgends in einen Nachbarmonat kippt.
const MONATSNAME = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const MONATSNAME_KURZ = new Intl.DateTimeFormat('de-DE', { month: 'long', timeZone: 'UTC' });

/** "Oktober 2026"; mit `kurz` nur "Oktober" fuer die Kacheln, wo das Jahr nichts beitraegt. */
export function alsMonat(monat: string, kurz = false): string {
  const [jahr, mm] = monat.split('-').map(Number) as [number, number];
  return (kurz ? MONATSNAME_KURZ : MONATSNAME).format(new Date(Date.UTC(jahr, mm - 1, 15)));
}

function leereZaehlung(): Record<AuftragsTyp, number> {
  return { ratgeber: 0, analyse: 0, pdp: 0, bilder: 0 };
}

/**
 * Monatsnutzung aus den schon geladenen Auftraegen, neuester Monat zuerst, hoechstens `monate`
 * Monate ab dem laufenden. Gezaehlt wird, was geladen ist, und als exakte Zahl gezeigt -- ohne
 * "mindestens" (Vorgabe 2026-10-16).
 *
 * Monate ohne einen Durchlauf stehen mit 0 da, solange sie nicht vor dem ersten geladenen
 * Auftrag liegen -- eine Reihe Nullen aus der Zeit vor dem Start waere keine Aussage.
 */
export function nutzungNachMonat(
  auftraege: readonly Auftrag[],
  filter: NutzungsFilter,
  jetzt: Date,
  monate: number,
): Monatsnutzung[] {
  const aktuell = monatVon(jetzt);
  // Der aelteste Monat im GELADENEN Bestand, ungefiltert: Er bestimmt, wie weit die Daten
  // reichen. Unter einem Filter waere es sonst der aelteste Treffer, und ein Monat davor mit
  // echten Null-Durchlaeufen fiele weg.
  let aeltester: string | null = null;
  const zeilen = new Map<
    string,
    { durchlaeufe: number; jeTyp: Record<AuftragsTyp, number>; uids: Set<string> }
  >();
  for (const a of auftraege) {
    const zeitpunkt = Date.parse(a.angelegtAm);
    if (Number.isNaN(zeitpunkt)) continue;
    const monat = monatVon(new Date(zeitpunkt));
    if (aeltester === null || monat < aeltester) aeltester = monat;
    if (filter.typ !== 'alle' && a.typ !== filter.typ) continue;
    if (filter.person !== null && a.ownerUid !== filter.person) continue;
    let zeile = zeilen.get(monat);
    if (!zeile) {
      zeile = { durchlaeufe: 0, jeTyp: leereZaehlung(), uids: new Set() };
      zeilen.set(monat, zeile);
    }
    zeile.durchlaeufe += 1;
    zeile.jeTyp[a.typ] += 1;
    zeile.uids.add(a.ownerUid);
  }

  const ergebnis: Monatsnutzung[] = [];
  for (let i = 0; i < monate; i += 1) {
    const monat = vorMonat(aktuell, i);
    // Leerer Bestand: nur der laufende Monat, mit 0. Sonst endet die Reihe am aeltesten Monat.
    if (aeltester === null ? i > 0 : monat < aeltester) break;
    const zeile = zeilen.get(monat);
    ergebnis.push({
      monat,
      durchlaeufe: zeile?.durchlaeufe ?? 0,
      jeTyp: zeile?.jeTyp ?? leereZaehlung(),
      personen: zeile?.uids.size ?? 0,
    });
  }
  return ergebnis;
}

/**
 * Balken mit Strich bei 200 (Rueckmeldung 2026-10-16): Der Strich bleibt auch ueber dem
 * Kontingent sichtbar. Darunter reicht die Skala ein Viertel ueber das Kontingent, damit der
 * Strich nicht am Rand klebt; darueber waechst sie mit, und der Strich rueckt nach links.
 * Beide Werte in Prozent der Balkenbreite.
 */
export function kontingentSkala(durchlaeufe: number): { fuellung: number; strich: number } {
  const skala = Math.max(NUTZUNGSKONTINGENT * 1.25, durchlaeufe);
  return {
    fuellung: (Math.max(0, durchlaeufe) / skala) * 100,
    strich: (NUTZUNGSKONTINGENT / skala) * 100,
  };
}
